import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import { useDispatch, useSelector } from 'react-redux';
import { getBookingDetails, cancelBooking } from '../../../store/slices/bookingSlice';

const BookingDetailsPage = () => {
  const { bookingToken: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const queryToken = searchParams.get('booking_token');
  const bookingToken = pathToken || queryToken;

  const isStandalonePage = location.pathname.includes('/flightAllDetails');

  const { selectedBooking, bookingDetailsLoading } = useSelector(
    (state) => state.bookings
  );

  const booking = selectedBooking;
  const loading = bookingDetailsLoading;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null); // Reference number
  const [isCancelling, setIsCancelling] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.manage-flight-dropdown')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (bookingToken) {
      dispatch(getBookingDetails(bookingToken));
    }
  }, [bookingToken, dispatch]);

  const handleCancelClick = () => {
    if (!booking) return;
    setCancellingBooking(booking.reference_number);
    setIsDropdownOpen(false);
  };

  const handleConfirmCancel = async () => {
    if (!booking || isCancelling) return;

    setIsCancelling(true);
    try {
      const resultAction = await dispatch(cancelBooking({
        bookingToken: booking.booking_token,
        reason: "User requested cancellation"
      }));

      if (cancelBooking.fulfilled.match(resultAction)) {
        toast.success(`Booking ${booking.reference_number} cancelled successfully.`);
        setCancellingBooking(null);
        navigate('/jetsetter/profile/booking-history');
      } else {
        throw new Error(resultAction.payload || "Failed to cancel booking");
      }
    } catch (error) {
      toast.error(error.message || "Failed to cancel booking. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleModifyBooking = () => {
    if (!booking) return;

    // Check if flight is already changed
    if (booking.parent_booking_id) {
      toast.error('Flight once already changed, Please contact admin for more changes');
      return;
    }

    // Navigate to flight search page with booking token
    navigate(`/search-flight?booking_token=${booking.booking_token}`);
  };

  const handleDownloadReceipt = () => {
    if (!booking) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Logo drawing
    doc.setFillColor(176, 0, 21); // #b00015
    const logoScale = 0.08;
    const logoStartX = pageWidth - 65;
    const logoStartY = 10;

    // Draw the 3 red shards from SVG
    // Shard 2 (middle top)
    doc.lines([
      [45.78 * logoScale, 0],
      [86.59 * logoScale, 42.15 * logoScale],
      [54.12 * logoScale, 42.15 * logoScale],
      [22.47 * logoScale, 9.52 * logoScale],
      [0, 0]
    ], logoStartX + (40 * logoScale), logoStartY, [1, 1], 'F', true);

    // Shard 1 (right)
    doc.lines([
      [92.17 * logoScale, 0],
      [125.04 * logoScale, 33.96 * logoScale],
      [112.51 * logoScale, 42.15 * logoScale],
      [40.81 * logoScale, 42.15 * logoScale],
      [0, 0]
    ], logoStartX + (127.41 * logoScale), logoStartY + (42.15 * logoScale), [1, 1], 'F', true);

    // Shard 3 (bottom left)
    doc.lines([
      [31.58 * logoScale, 32.63 * logoScale],
      [-8.93 * logoScale, 32.63 * logoScale],
      [-49.75 * logoScale, -9.52 * logoScale],
      [-18.17 * logoScale, -9.52 * logoScale],
      [0, 0]
    ], logoStartX + (95.55 * logoScale), logoStartY + (84.31 * logoScale), [1, 1], 'F', true);

    // JETSCANNER360 Text
    doc.setTextColor(14, 13, 9); // #0e0d09
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('JETSCANNER360', logoStartX + 22, logoStartY + 8);

    // Header styling - Confirmation Box
    doc.setFillColor(43, 0, 86); // Dark purple from screenshot
    doc.rect(10, 10, 100, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Confirmation', 12, 16);
    doc.text('Receipt/Invoice No.', 12, 20);
    doc.text(booking.invoice_no || 'N/A', 60, 18);

    // Transaction ID row
    doc.setFillColor(255, 255, 255);
    doc.rect(10, 22, 100, 8, 'S');
    doc.setTextColor(0, 0, 0);
    doc.text('Transaction ID:', 12, 27);
    doc.setFont('helvetica', 'normal');
    doc.text(booking.transaction_id || 'N/A', 40, 27);

    // Grid headers
    doc.setFillColor(43, 0, 86);
    doc.rect(10, 30, 100, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('Booking Date:', 12, 35);
    doc.text('Passenger(s)', 45, 35);
    doc.text('Fare/Pax', 75, 35);
    doc.text('Total', 95, 35);

    // Grid data
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    const bookingDate = new Date(booking.created_at).toLocaleString();
    doc.text(bookingDate, 12, 43);
    doc.text(booking.passenger_count.toString(), 50, 43);
    doc.text(`$${(parseFloat(booking.total_amount.split(' ')[1]) / booking.passenger_count).toLocaleString()}`, 75, 43);
    doc.text(`${booking.total_amount}`, 95, 43);

    // Booking Ref
    doc.setFont('helvetica', 'bold');
    doc.text(`Booking Ref: ${booking.reference_number}`, 12, 53);
    doc.text(`e-Ticket No.: ${booking.e_ticket_number || 'Pending'}`, 12, 58);

    // Flight Info Box
    doc.setFillColor(43, 0, 86);
    doc.rect(10, 63, 40, 6, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('Flight 01', 12, 67);

    doc.setTextColor(0, 0, 0);
    doc.text(`Status : ${booking.booking_status}`, 12, 75);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(bookingDate, 12, 80);
    doc.text(booking.flight_number, 12, 85);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(`${booking.departure.airport} ${booking.departure.iata}`, 12, 92);
    doc.text('->', 60, 92);
    doc.text(`${booking.arrival.airport} ${booking.arrival.iata}`, 75, 92);

    doc.setFont('helvetica', 'normal');
    doc.text(new Date(booking.departure.datetime).toLocaleString(), 12, 98);
    doc.text(`${Math.floor(booking.duration_minutes / 60)} Hrs ${booking.duration_minutes % 60} Mins (flight time)`, 50, 98);
    doc.text(new Date(booking.arrival.datetime).toLocaleString(), 85, 98);

    doc.setFontSize(7);
    doc.text('* All times displayed are local', 60, 105, { align: 'center' });

    let currentY = 115;

    // Passenger(s) Header
    doc.setFillColor(43, 0, 86);
    doc.rect(10, currentY, 30, 6, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Passenger(s)', 12, currentY + 4);

    currentY += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    booking.passengers.forEach((p, idx) => {
      doc.text(`${idx + 1}. ${p.first_name} ${p.last_name} (${p.title || 'Mister'})`, 12, currentY);
      doc.text(`Email: ${p.email} | Mobile: ${p.mobile_number}`, 12, currentY + 4);
      currentY += 10;

      // Check if we need a new page (unlikely for few pax, but safe)
      if (currentY > 270) {
        doc.addPage();
        currentY = 20;
      }
    });

    // Footer
    currentY = Math.max(currentY + 10, 140);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128); // Gray color for disclaimer
    doc.text('This receipt is automatically generated. No signature required.', pageWidth / 2, currentY, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Jetscanner360.com -- GAJR Technologies Pte. Ltd.', pageWidth / 2, currentY + 8, { align: 'center' });

    doc.save(`Receipt-${booking.reference_number}.pdf`);
    toast.success('Receipt downloaded successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
          <p className="text-jet-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-jet-gray-900 mb-4">Booking Not Found</h2>
        <p className="text-jet-gray-600 mb-6">The booking you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/jetsetter/profile/booking-history')}
          className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium px-6 py-3 rounded-md"
        >
          Back to Bookings
        </button>
      </div>
    );
  }

  return (
    <div className={`max-w-6xl mx-auto px-4 ${isStandalonePage ? 'pt-[100px]' : 'pt-6'} pb-12`}>
      {/* Container with shadow and white bg like the screenshot */}
      <div className="bg-white rounded shadow-sm border border-gray-100 px-8 pt-8 pb-10 space-y-4">
        {/* Header Summary */}
        <div className="space-y-1">
          <h1 className="text-base font-bold text-gray-900">
            Booking Ref: {booking.reference_number}
          </h1>
          <h2 className="text-base font-bold text-gray-900">{booking.flight_number}</h2>
        </div>

        {/* Buttons Row (Aligned right in the same visual area) */}
        <div className="flex justify-end space-x-2 -mt-12">
          <button
            onClick={() => navigate(-1)}
            className="bg-jet-yellow hover:bg-yellow-500 text-gray-900 font-bold px-4 py-1.5 text-sm rounded flex items-center shadow-sm"
          >
            <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Back
          </button>
          <div className="relative manage-flight-dropdown">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
              className="bg-jet-yellow hover:bg-yellow-500 text-gray-900 font-bold px-4 py-1.5 text-sm rounded flex items-center shadow-sm"
            >
              Manage Flight
              <svg className={`w-3.5 h-3.5 ml-1 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-1 w-[220px] bg-jet-yellow rounded shadow-xl overflow-hidden z-50 border border-yellow-500">
                <button
                  onClick={() => { setIsDropdownOpen(false); setShowReceiptModal(true); }}
                  className="w-full text-left px-4 py-3 text-sm font-bold text-gray-900 hover:bg-yellow-500 flex items-center gap-2 border-b border-black/5"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  Receipts & Invoices
                </button>
                {booking.booking_status === 'Upcoming' && (
                  <>
                    {(() => {
                      const hoursToDeparture = (new Date(booking.departure.datetime).getTime() - new Date().getTime()) / (1000 * 60 * 60);
                      const isWithin24Hours = hoursToDeparture <= 24;
                      const hasBeenChanged = !!booking.parent_booking_id;

                      let disabledReason = '';
                      if (isWithin24Hours) {
                        disabledReason = 'Flights cannot be changed within 24 hours of departure.';
                      } else if (hasBeenChanged) {
                        disabledReason = 'This flight has already been changed. Please contact admin for further changes.';
                      }

                      return (
                        <div title={disabledReason}>
                          <button
                            onClick={() => {
                              if (!disabledReason) {
                                setIsDropdownOpen(false);
                                handleModifyBooking();
                              }
                            }}
                            disabled={!!disabledReason}
                            className={`w-full text-left px-4 py-3 text-sm font-bold flex items-center gap-2 border-b border-black/5 ${disabledReason ? 'text-gray-500 cursor-not-allowed opacity-60' : 'text-gray-900 hover:bg-yellow-500'}`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
                            </svg>
                            Change Flight
                          </button>
                        </div>
                      );
                    })()}
                    {(() => {
                      const now = new Date().getTime();
                      const departureTime = new Date(booking.departure.datetime).getTime();
                      const hoursToDeparture = (departureTime - now) / (1000 * 60 * 60);
                      const totalAmount = parseFloat(booking.total_amount.split(' ')[1]) || 0;
                      const cancellationFee = booking.cancellation_fee || 250;

                      let cancelDisabledReason = '';
                      if (hoursToDeparture <= 1) {
                        cancelDisabledReason = 'Flights cannot be cancelled within 1 hour of departure.';
                      } else if (totalAmount <= cancellationFee) {
                        cancelDisabledReason = `This booking cannot be cancelled because the total fare ($${totalAmount}) is less than or equal to the cancellation fee ($${cancellationFee}).`;
                      }

                      return (
                        <div title={cancelDisabledReason}>
                          <button
                            onClick={() => {
                              if (!cancelDisabledReason) {
                                handleCancelClick();
                              }
                            }}
                            disabled={!!cancelDisabledReason}
                            className={`w-full text-left px-4 py-3 text-sm font-bold flex items-center gap-2 transition-colors ${cancelDisabledReason ? 'text-gray-500 cursor-not-allowed opacity-60' : 'text-gray-900 hover:bg-yellow-500'}`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="15" y1="9" x2="9" y2="15"></line>
                              <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                            Cancel Flight
                          </button>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Compact Flight Summary Row */}
        <div className="border border-gray-100 rounded p-4 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1 flex items-center">
            {/* Departure */}
            <div className="min-w-[120px]">
              <div className="text-sm font-bold text-gray-900">
                {booking.departure.iata} {new Date(booking.departure.datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {new Date(booking.departure.datetime).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  year: '2-digit'
                }).replace(',', '')}
              </div>
            </div>

            {/* Arrow/Duration */}
            <div className="flex-1 flex flex-col items-center px-4">
              <div className="w-full flex items-center relative">
                <div className="flex-1 h-[1px] bg-gray-300"></div>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                  <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="text-[10px] font-bold text-gray-500 uppercase mt-1 tracking-tight">
                {Math.floor(booking.duration_minutes / 60)} Hrs {booking.duration_minutes % 60} Mins
              </div>
            </div>

            {/* Arrival */}
            <div className="min-w-[120px] text-right md:text-left">
              <div className="text-sm font-bold text-gray-900">
                {booking.arrival.iata} {new Date(booking.arrival.datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {new Date(booking.arrival.datetime).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  year: '2-digit'
                }).replace(',', '')}
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="hidden md:block w-[1px] h-10 bg-gray-100 mx-2"></div>

          {/* Summary Row */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]">
            <div className="flex items-center">
              <span className="text-gray-500 font-medium whitespace-nowrap">Passenger(s) : </span>
              <span className="text-gray-900 font-bold ml-1">{booking.passenger_count}</span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-500 font-medium whitespace-nowrap">Fare/Pax </span>
              <span className="text-gray-900 font-bold ml-1">${(parseFloat(booking.total_amount.split(' ')[1]) / booking.passenger_count).toLocaleString()}</span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-500 font-medium whitespace-nowrap">Total Fare </span>
              <span className="text-gray-900 font-bold ml-1">${parseFloat(booking.total_amount.split(' ')[1]).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Passenger(s) Label */}
        <div className="mt-6">
          <h3 className="text-sm font-bold text-[#8B3D3D] mb-4">Passenger(s)</h3>
          <div className="space-y-4">
            {booking.passengers.map((passenger, index) => (
              <div key={passenger.id} className="bg-white border border-gray-100 rounded p-4 flex flex-col md:flex-row md:items-start justify-between gap-4 shadow-sm">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-4 shrink-0 mt-0.5">
                    {index + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900 uppercase tracking-tight">
                      {passenger.first_name} {passenger.last_name}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {passenger.title || 'Mister'} • DOB: {new Date(passenger.date_of_birth).toLocaleDateString('en-GB')}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-x-12 gap-y-4 pr-12">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email</span>
                    <span className="text-xs text-gray-700 font-bold">{passenger.email}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Mobile</span>
                    <span className="text-xs text-gray-700 font-bold">{passenger.mobile_number}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[60] p-4 overflow-y-auto pt-24 pb-12">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowReceiptModal(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div className="w-[300px]">
                  <div className="bg-[#2B0056] text-white p-3 flex justify-between items-center rounded-sm">
                    <div className="text-[10px] font-bold uppercase leading-tight">
                      Confirmation<br />Receipt/Invoice No.
                    </div>
                    <div className="text-sm font-bold">{booking.invoice_no}</div>
                  </div>
                  <div className="border border-[#2B0056] p-2 flex items-center mt-[-1px]">
                    <span className="text-[10px] font-bold text-gray-900 mr-2">Transaction ID:</span>
                    <span className="text-[10px] text-gray-600 truncate">{booking.transaction_id}</span>
                  </div>
                </div>
                <img src="/jetscanner-logo.svg" alt="JetScanner360" className="h-8 mt-2" />
              </div>

              <div className="bg-[#2B0056] text-white p-2 grid grid-cols-4 gap-2 text-[10px] font-bold rounded-sm">
                <div>Booking Date:</div>
                <div className="text-center">Passenger(s)</div>
                <div className="text-center">Fare/Pax</div>
                <div className="text-center">Total</div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-[10px] text-gray-700 px-2">
                <div>{new Date(booking.created_at).toLocaleString()}</div>
                <div className="text-center">{booking.passenger_count}</div>
                <div className="text-center">${(parseFloat(booking.total_amount.split(' ')[1]) / booking.passenger_count).toLocaleString()}</div>
                <div className="text-center">{booking.total_amount}</div>
              </div>

              <div className="space-y-1 mt-4">
                <div className="text-xs font-bold text-gray-900">Booking Ref: {booking.reference_number}</div>
                <div className="text-xs font-bold text-gray-900">e-Ticket No.: {booking.e_ticket_number || 'Pending'}</div>
              </div>

              <div className="mt-6">
                <div className="bg-[#2B0056] text-white px-4 py-1.5 text-xs font-bold inline-block rounded-sm">
                  Flight 01
                </div>
                <div className="border-t-[3px] border-[#2B0056] mt-[-1px] pt-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="text-xs font-bold text-gray-900">Status : {booking.booking_status}</div>
                      <div className="text-[10px] text-gray-500">{new Date(booking.created_at).toLocaleString()}</div>
                      <div className="text-xs font-bold text-gray-900">{booking.flight_number}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex-1 text-sm font-bold text-gray-900">
                      {booking.departure.airport} {booking.departure.iata}
                    </div>
                    <div className="px-4 flex items-center text-gray-400">
                      →
                    </div>
                    <div className="flex-1 text-right text-sm font-bold text-gray-900">
                      {booking.arrival.airport} {booking.arrival.iata}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-1 text-[10px] text-gray-600">
                    <div>{new Date(booking.departure.datetime).toLocaleString()}</div>
                    <div className="font-bold">{Math.floor(booking.duration_minutes / 60)} Hrs {booking.duration_minutes % 60} Mins (flight time)</div>
                    <div>{new Date(booking.arrival.datetime).toLocaleString()}</div>
                  </div>

                  <div className="text-center mt-4 text-[9px] font-bold text-gray-500 italic">
                    * All times displayed are local
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-100 italic space-y-4">
                <div className="bg-[#2B0056] text-white px-4 py-1 text-xs font-bold inline-block rounded-sm not-italic">
                  Passenger(s)
                </div>
                <div className="space-y-3 not-italic">
                  {booking.passengers.map((p, idx) => (
                    <div key={p.id} className="text-[11px] border-b border-gray-50 pb-2">
                      <div className="font-bold text-gray-900">{idx + 1}. {p.first_name} {p.last_name} ({p.title || 'Mister'})</div>
                      <div className="text-gray-500 mt-0.5">Email: {p.email} | Mobile: {p.mobile_number}</div>
                    </div>
                  ))}
                </div>
                <div className="text-center text-[10px] text-gray-500">
                  This receipt is automatically generated. No signature required.
                </div>
                <div className="text-center text-[10px] font-bold text-gray-700">
                  Jetscanner360.com -- GAJR Technologies Pte. Ltd.
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={handleDownloadReceipt}
                className="bg-jet-yellow hover:bg-yellow-500 text-gray-900 font-bold px-8 py-2 rounded shadow-sm flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Receipt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Confirmation Modal - Styled per screenshot */}
      {cancellingBooking && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-[10px] shadow-2xl max-w-[400px] w-full overflow-hidden transform animate-in zoom-in-95 duration-200">
            {/* Modal Content */}
            <div className="p-8 flex flex-col items-center">
              {/* Red Triangle Icon */}
              <div className="w-16 h-16 bg-white flex items-center justify-center mb-6">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L1 21H23L12 2Z" fill="#F04438" stroke="#F04438" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M12 9V13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 17H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-jet-gray-900 mb-3 text-center">Are you sure?</h3>

              <div className="text-center text-jet-gray-600 text-sm leading-relaxed mb-8">
                You are about to Cancel this Flight. Click "YES" to proceed or "CANCEL" to exit.
                In proceeding to cancel this flight, a cancellation fee of
                <span className="font-bold text-jet-gray-900"> ${(booking.cancellation_fee || 250).toLocaleString()} </span>
                is applicable.
              </div>

              <div className="flex gap-3 w-full">
                <button
                  onClick={handleConfirmCancel}
                  disabled={isCancelling}
                  className={`flex-1 bg-[#F04438] text-white font-bold py-2.5 px-6 rounded-[5px] hover:bg-red-700 transition-colors shadow-lg shadow-red-200 uppercase tracking-wider flex items-center justify-center ${isCancelling ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isCancelling ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : 'YES'}
                </button>
                <button
                  onClick={() => setCancellingBooking(null)}
                  disabled={isCancelling}
                  className="flex-1 bg-white text-jet-gray-700 font-bold py-2.5 px-6 rounded-[5px] border border-jet-gray-300 hover:bg-gray-50 transition-colors uppercase tracking-wider"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetailsPage;